import { useEffect, useState, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import {
  Check,
  Database,
  FileText,
  Lock,
  Minus,
  PenLine,
  Search,
  Sparkles,
  Workflow,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import type { UiFragmentKind } from '@/data/platform'
import { BRAND, OUT_EASE, halftone, type BrandColor } from './halftone'

/* ----------------------------------------------------------------------------
   Product UI fragments for the platform page's pinned stage. Each is a square
   stage (grey ground, halftone brand block, white app window) drawn in JSX in
   place of photography. Text sizes use container units so the fragment scales
   with the stage. `active` starts the fragment's one small motion (streaming
   text, fields filling, bars rising); progress is kept when it pauses.
   Decorative: aria-hidden.
---------------------------------------------------------------------------- */

export type { UiFragmentKind }

export interface UiFragmentProps {
  kind: UiFragmentKind
  /** Use-case title; tunes the fragment's labels. */
  title: string
  /** Use-case industry; tunes data sources (CRM vs HRIS). */
  industry?: string
  demo?: { ask: string; answer: string }
  active: boolean
  accent?: BrandColor
}

/** True from the first time `active` is true. */
function useSeen(active: boolean) {
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    if (active) setSeen(true)
  }, [active])
  return seen
}

/** Types `text` out while `run` is true; keeps its place when paused. */
function useStream(text: string, run: boolean) {
  const reduced = useReducedMotion()
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (reduced || !run || count >= text.length) return
    const id = window.setTimeout(() => setCount((c) => Math.min(text.length, c + 2)), 26)
    return () => window.clearTimeout(id)
  }, [reduced, run, count, text.length])
  return { shown: reduced ? text : text.slice(0, count), done: reduced || count >= text.length }
}

const muted = 'text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]'
const strong = 'text-[var(--color-landing-gray-900)] dark:text-white'
const line = 'block h-[0.55em] bg-[var(--color-landing-gray-200)] dark:bg-[var(--color-landing-gray-700)]'
const hair = 'border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-700)]'

function Stage({ children, accent = 'pink' }: { children: ReactNode; accent?: BrandColor }) {
  return (
    <div
      aria-hidden
      className="relative h-full w-full overflow-hidden bg-[#f3f3f3] [container-type:inline-size] dark:bg-[#15151d]"
    >
      <span className="absolute right-0 top-0 h-[46%] w-[46%]" style={{ backgroundColor: BRAND[accent], ...halftone(0.3, 6) }} />
      <span className="absolute bottom-[5%] left-[4%] h-[18%] w-[18%] border border-[var(--color-landing-gray-300)] dark:border-[var(--color-landing-gray-700)]" />
      <div
        className={cn(
          'absolute bottom-[9%] left-[7%] right-[11%] top-[11%] flex flex-col overflow-hidden bg-white text-[clamp(9px,2.1cqw,15px)] leading-[1.35] shadow-[0_30px_60px_-24px_rgba(10,10,10,0.35)] dark:bg-[#1c1c26]',
          strong
        )}
      >
        {children}
      </div>
    </div>
  )
}

function WindowBar({ app, icon, title, meta }: { app?: OfficeApp; icon?: ReactNode; title: string; meta?: string }) {
  return (
    <div className={cn('flex items-center gap-[0.6em] border-b px-[1em] py-[0.7em]', hair)}>
      {app ? <OfficeAppIcon app={app} className="h-[1.9em] w-[1.9em]" /> : icon}
      <span className="font-semibold">{title}</span>
      {meta && <span className={cn('ml-auto text-[0.85em]', muted)}>{meta}</span>}
    </div>
  )
}

function Tick({ show, delay = 0 }: { show: boolean; delay?: number }) {
  return (
    <motion.span
      className="flex h-[1.4em] w-[1.4em] items-center justify-center text-white"
      style={{ backgroundColor: BRAND.green }}
      initial={false}
      animate={show ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
      transition={{ duration: 0.35, ease: OUT_EASE, delay: show ? delay : 0 }}
    >
      <Check className="h-[0.95em] w-[0.95em]" strokeWidth={3} />
    </motion.span>
  )
}

/* -------------------------------- prompt --------------------------------- */
function PromptFragment({ demo, active }: UiFragmentProps) {
  const ask = demo?.ask ?? 'Draft a first version from our approved template'
  const answer = demo?.answer ?? 'Drafted from your template, in your brand voice.'
  const { shown, done } = useStream(answer, active)
  const progress = answer.length ? shown.length / answer.length : 1
  const draft = [70, 100, 94, 100, 62, 100, 88, 45]
  return (
    <Stage accent="purple">
      <WindowBar icon={<Sparkles className="h-[1.4em] w-[1.4em] text-[var(--color-landing-accent)]" />} title="Templatean AI" meta="Word" />
      <div className="flex flex-1 flex-col gap-[1em] p-[1.2em]">
        <div className={cn('border p-[0.9em]', hair)}>
          <span className={cn('block text-[0.8em] font-semibold uppercase tracking-wide', muted)}>Prompt</span>
          <span className="mt-[0.3em] block font-medium">{ask}</span>
        </div>
        <div className="min-h-[4.2em] bg-[var(--color-landing-gray-50)] p-[0.9em] font-[family-name:var(--font-landing-mono)] text-[0.92em] dark:bg-[#15151d]">
          {shown}
          {!done && <span className="ml-[1px] inline-block h-[1em] w-[0.5em] translate-y-[0.15em] bg-[var(--color-landing-accent)]" />}
        </div>
        <div className={cn('flex flex-1 flex-col gap-[0.75em] border p-[0.9em]', hair)}>
          <span className={cn('text-[0.8em] font-semibold uppercase tracking-wide', muted)}>Draft</span>
          {draft.map((w, i) => (
            <span
              key={i}
              className={cn(line, 'origin-left transition-[transform,opacity] duration-300', i % 4 === 0 && 'bg-[var(--color-landing-gray-400)] dark:bg-[var(--color-landing-gray-500)]')}
              style={{
                width: `${w}%`,
                opacity: progress >= (i + 1) / draft.length ? 1 : 0,
                transform: `scaleX(${progress >= (i + 1) / draft.length ? 1 : 0.2})`,
              }}
            />
          ))}
        </div>
        <div className="flex items-center gap-[0.6em]">
          <span className="bg-[var(--color-landing-primary)] px-[1em] py-[0.55em] text-[0.85em] font-bold uppercase text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
            Insert
          </span>
          <span className={cn('text-[0.85em]', muted)}>{done ? 'Ready to insert' : 'Writing…'}</span>
        </div>
      </div>
    </Stage>
  )
}

/* --------------------------------- form ---------------------------------- */
function formFields(title: string, industry = ''): Array<[string, string]> {
  if (/hr|human|onboard/i.test(`${title} ${industry}`))
    return [
      ['Employee', '{{hris.name}}'],
      ['Role', '{{hris.title}}'],
      ['Start date', '{{hris.start_date}}'],
      ['Manager', '{{hris.manager}}'],
    ]
  if (/contract|legal/i.test(`${title} ${industry}`))
    return [
      ['Party', '{{party.name}}'],
      ['Term', '{{contract.term}}'],
      ['Governing law', '{{contract.law}}'],
      ['Fee', '{{pricing.total}}'],
    ]
  return [
    ['Client', '{{crm.account}}'],
    ['Contact', '{{crm.contact}}'],
    ['Deal value', '{{crm.amount}}'],
    ['Close date', '{{crm.close_date}}'],
  ]
}

function FormFragment({ title, industry, active }: UiFragmentProps) {
  const seen = useSeen(active)
  const fields = formFields(title, industry)
  return (
    <Stage accent="blue">
      <WindowBar app="word" title={title} meta="Variables" />
      <div className="grid flex-1 grid-cols-[1.1fr_1fr]">
        <div className={cn('flex flex-col justify-between gap-[0.85em] border-r p-[1.1em]', hair)}>
          {fields.map(([label, token], i) => (
            <div key={label}>
              <span className={cn('block text-[0.8em] font-semibold uppercase tracking-wide', muted)}>{label}</span>
              <motion.span
                className="mt-[0.3em] block border px-[0.6em] py-[0.35em] font-[family-name:var(--font-landing-mono)] text-[0.85em]"
                initial={false}
                animate={
                  seen
                    ? { backgroundColor: 'rgba(12,0,255,0.08)', borderColor: 'rgba(12,0,255,0.5)' }
                    : { backgroundColor: 'rgba(12,0,255,0)', borderColor: 'rgba(160,160,160,0.4)' }
                }
                transition={{ duration: 0.4, ease: OUT_EASE, delay: seen ? 0.3 + i * 0.22 : 0 }}
              >
                {token}
              </motion.span>
            </div>
          ))}
          <span className="bg-[var(--color-landing-primary)] py-[0.55em] text-center text-[0.85em] font-bold uppercase text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
            Generate
          </span>
        </div>
        <div className="flex flex-col gap-[0.7em] p-[1.1em]">
          <span className={cn(line, 'w-2/3 bg-[var(--color-landing-gray-900)] dark:bg-white')} />
          {fields.map(([label], i) => (
            <span key={label} className="flex items-center gap-[0.4em]">
              <span className={cn(line, 'w-1/3')} />
              <motion.span
                className="block h-[0.7em] w-1/2 origin-left bg-[var(--color-landing-accent)]"
                initial={false}
                animate={{ scaleX: seen ? 1 : 0 }}
                transition={{ duration: 0.45, ease: OUT_EASE, delay: seen ? 0.45 + i * 0.22 : 0 }}
              />
            </span>
          ))}
          {[90, 100, 72, 84, 100, 66, 92].map((w, i) => (
            <span key={i} className={line} style={{ width: `${w}%` }} />
          ))}
          <span className="mt-auto flex flex-col gap-[0.4em]">
            <span className={cn('text-[0.75em] font-semibold uppercase tracking-wide', muted)}>Signature</span>
            <span className="block h-px w-2/3 bg-[var(--color-landing-gray-400)]" />
          </span>
        </div>
      </div>
    </Stage>
  )
}

/* --------------------------------- chart --------------------------------- */
const BARS = [42, 58, 50, 74, 66, 92]

function ChartFragment({ title, active }: UiFragmentProps) {
  const seen = useSeen(active)
  return (
    <Stage accent="pink">
      <WindowBar app="word" title={title} meta="Report" />
      <div className="flex flex-1 flex-col gap-[1em] p-[1.2em]">
        <div className={cn('flex min-h-0 flex-1 items-end gap-[6%] border-b border-l px-[4%] pt-[4%]', hair)}>
          {BARS.map((h, i) => (
            <motion.span
              key={i}
              className="block flex-1 origin-bottom"
              style={{ height: `${h}%`, backgroundColor: i === BARS.length - 1 ? BRAND.pink : BRAND.blue }}
              initial={false}
              animate={{ scaleY: seen ? 1 : 0.15 }}
              transition={{ duration: 0.7, ease: OUT_EASE, delay: seen ? 0.3 + i * 0.07 : 0 }}
            />
          ))}
        </div>
        <div className="flex gap-[0.7em] bg-[var(--color-landing-gray-50)] p-[0.8em] dark:bg-[#15151d]">
          <Sparkles className="mt-[0.1em] h-[1.3em] w-[1.3em] shrink-0 text-[var(--color-landing-accent)]" />
          <span className="flex flex-1 flex-col gap-[0.5em]">
            <span className={cn(line, 'w-full')} />
            <span className={cn(line, 'w-4/5')} />
          </span>
        </div>
        {[100, 94, 70].map((w, i) => (
          <span key={i} className={line} style={{ width: `${w}%` }} />
        ))}
      </div>
    </Stage>
  )
}

/* -------------------------------- locked --------------------------------- */
function LockedFragment({ title, active }: UiFragmentProps) {
  const seen = useSeen(active)
  const sections = [
    { locked: true, label: 'Brand header' },
    { locked: false, label: 'Client details' },
    { locked: true, label: 'Standard clauses' },
    { locked: false, label: 'Scope' },
  ]
  return (
    <Stage accent="purple">
      <WindowBar app="word" title={title} meta="Template" />
      <div className="flex flex-1 flex-col gap-[0.8em] p-[1.2em]">
        {sections.map((s, i) => (
          <div
            key={s.label}
            className={cn(
              'flex flex-1 items-start gap-[0.7em] p-[0.8em]',
              s.locked
                ? 'bg-[var(--color-landing-gray-100)] dark:bg-[#15151d]'
                : 'border border-dashed border-[var(--color-landing-accent)]'
            )}
          >
            <motion.span
              className="flex h-[1.8em] w-[1.8em] shrink-0 items-center justify-center text-white"
              style={{ backgroundColor: s.locked ? BRAND.purple : BRAND.blue }}
              initial={false}
              animate={seen ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.4, ease: OUT_EASE, delay: seen ? 0.3 + i * 0.12 : 0 }}
            >
              {s.locked ? <Lock className="h-[1em] w-[1em]" /> : <PenLine className="h-[1em] w-[1em]" />}
            </motion.span>
            <span className="flex flex-1 flex-col gap-[0.45em]">
              <span className="flex items-center justify-between font-semibold">
                {s.label}
                <span className={cn('text-[0.78em] font-semibold uppercase tracking-wide', muted)}>
                  {s.locked ? 'Locked' : 'Editable'}
                </span>
              </span>
              <span className={cn(line, 'w-full')} />
              <span className={cn(line, 'w-3/4')} />
            </span>
          </div>
        ))}
      </div>
    </Stage>
  )
}

/* --------------------------------- flow ---------------------------------- */
function FlowFragment({ title, industry, active }: UiFragmentProps) {
  const seen = useSeen(active)
  const reduced = useReducedMotion()
  const hr = /hr|human|onboard/i.test(`${title} ${industry}`)
  const source = hr ? 'HRIS' : 'CRM'
  const output = hr ? 'Onboarding packet' : 'Proposal'
  const log = hr
    ? ['New hire added in HRIS', 'Onboarding packet generated', 'Sent for signature']
    : ['Opportunity reached proposal stage', 'Proposal generated', 'Routed for approval']
  return (
    <Stage accent="green">
      <WindowBar icon={<Workflow className="h-[1.4em] w-[1.4em] text-[var(--color-landing-accent)]" />} title={title} meta="Automation" />
      <div className="flex flex-1 flex-col justify-between gap-[1em] p-[1.2em]">
        <div className="relative flex flex-1 items-center justify-between">
          <span className="absolute left-[12%] right-[12%] top-[42%] h-px bg-[var(--color-landing-gray-300)] dark:bg-[var(--color-landing-gray-600)]" />
          {seen && !reduced && (
            <span className="absolute left-[12%] right-[12%] top-[42%] z-10 h-0 overflow-visible">
              <motion.span
                className="relative block h-0 w-full"
                initial={{ x: '-100%' }}
                animate={{ x: ['-100%', '0%'] }}
                transition={{ duration: 2.4, ease: 'linear', repeat: Infinity, repeatDelay: 0.6 }}
              >
                <span className="absolute right-0 top-0 block h-[0.8em] w-[0.8em] -translate-y-1/2" style={{ backgroundColor: BRAND.pink }} />
              </motion.span>
            </span>
          )}
          {[
            { label: source, node: <Database className="h-[1.6em] w-[1.6em]" />, bg: 'bg-[var(--color-landing-gray-900)] dark:bg-white dark:text-[var(--color-landing-primary)]' },
            { label: 'Templatean', node: <span className="font-[family-name:var(--font-landing-display)] text-[1.2em]">T</span>, bg: '' },
            { label: output, node: <FileText className="h-[1.6em] w-[1.6em]" />, bg: 'bg-[var(--color-landing-secondary)]' },
          ].map((n, i) => (
            <span key={n.label} className="relative flex w-[26%] flex-col items-center gap-[0.5em] text-center">
              <span
                className={cn('flex aspect-square w-[62%] items-center justify-center text-white', n.bg)}
                style={i === 1 ? { backgroundColor: BRAND.blue, ...halftone(0.22, 5) } : undefined}
              >
                {n.node}
              </span>
              <span className="text-[0.85em] font-semibold">{n.label}</span>
            </span>
          ))}
        </div>
        <ul className={cn('flex flex-col border-t', hair)}>
          {log.map((entry, i) => (
            <li key={entry} className={cn('flex items-center gap-[0.7em] border-b py-[0.6em]', hair)}>
              <Tick show={seen} delay={0.4 + i * 0.35} />
              <span>{entry}</span>
            </li>
          ))}
        </ul>
      </div>
    </Stage>
  )
}

/* -------------------------------- assets --------------------------------- */
function AssetsFragment({ active }: UiFragmentProps) {
  const seen = useSeen(active)
  const tiles: Array<{ kind: 'logo' | 'swatch' | 'font' | 'image'; color: string; label?: string }> = [
    { kind: 'logo', color: BRAND.blue },
    { kind: 'swatch', color: BRAND.blue, label: '#0C00FF' },
    { kind: 'image', color: BRAND.purple },
    { kind: 'font', color: '' },
    { kind: 'logo', color: '#0a0a0a' },
    { kind: 'swatch', color: BRAND.pink, label: '#FF00F2' },
    { kind: 'image', color: BRAND.pink },
    { kind: 'swatch', color: BRAND.purple, label: '#B200FF' },
    { kind: 'logo', color: BRAND.purple },
  ]
  return (
    <Stage accent="blue">
      <WindowBar icon={<span className="block h-[1.4em] w-[1.4em]" style={{ backgroundColor: BRAND.pink, ...halftone(0.3, 4) }} />} title="Brand assets" meta="Approved" />
      <div className="flex flex-1 flex-col gap-[0.9em] p-[1.1em]">
        <div className={cn('flex items-center gap-[0.6em] border px-[0.8em] py-[0.5em]', hair)}>
          <Search className={cn('h-[1.1em] w-[1.1em]', muted)} />
          <span>logo</span>
          <span className="ml-auto flex gap-[0.4em] text-[0.8em] font-semibold uppercase">
            {['Logos', 'Colors', 'Fonts'].map((c, i) => (
              <span key={c} className={cn('px-[0.5em] py-[0.15em]', i === 0 ? 'bg-[var(--color-landing-accent)] text-white' : 'bg-[var(--color-landing-gray-100)] dark:bg-[#15151d]')}>
                {c}
              </span>
            ))}
          </span>
        </div>
        <div className="grid flex-1 grid-cols-3 gap-[0.6em]">
          {tiles.map((t, i) => (
            <motion.span
              key={i}
              className={cn(
                'relative flex items-end overflow-hidden p-[0.4em]',
                t.kind === 'font' || t.kind === 'logo' ? 'border bg-white dark:bg-[#15151d]' : '',
                hair,
                i === 0 && 'outline outline-2 outline-offset-2 outline-[var(--color-landing-accent)]'
              )}
              style={t.kind === 'swatch' ? { backgroundColor: t.color } : t.kind === 'image' ? { backgroundColor: t.color, ...halftone(0.35, 5) } : undefined}
              initial={false}
              animate={seen ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ duration: 0.45, ease: OUT_EASE, delay: seen ? 0.25 + i * 0.05 : 0 }}
            >
              {t.kind === 'logo' && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-[2.2em] w-[2.2em] items-center justify-center font-[family-name:var(--font-landing-display)] text-[1em] text-white" style={{ backgroundColor: t.color }}>
                    T
                  </span>
                </span>
              )}
              {t.kind === 'font' && <span className="absolute inset-0 flex items-center justify-center font-[family-name:var(--font-landing-display)] text-[1.5em]">Aa</span>}
              {t.label && <span className="relative font-[family-name:var(--font-landing-mono)] text-[0.72em] text-white">{t.label}</span>}
            </motion.span>
          ))}
        </div>
      </div>
    </Stage>
  )
}

/* --------------------------------- audit --------------------------------- */
function AuditFragment({ title, active }: UiFragmentProps) {
  const seen = useSeen(active)
  const rows = /contract/i.test(title)
    ? [
        ['09:12', 'Editor', 'Contract generated'],
        ['09:14', 'System', 'Routed for approval'],
        ['10:02', 'Approver', 'Approved'],
        ['10:03', 'System', 'Version saved'],
        ['10:05', 'Editor', 'Sent to client'],
      ]
    : [
        ['08:47', 'Editor', 'Template edited'],
        ['09:03', 'Approver', 'Change approved'],
        ['09:21', 'Viewer', 'Document exported'],
        ['09:40', 'Admin', 'Role updated'],
        ['10:02', 'Editor', 'Asset replaced'],
      ]
  return (
    <Stage accent="purple">
      <WindowBar icon={<Lock className="h-[1.3em] w-[1.3em] text-[var(--color-landing-secondary)]" />} title="Audit log" meta="All actions" />
      <div className="flex flex-1 flex-col p-[1.1em]">
        <div className={cn('grid grid-cols-[0.8fr_1fr_2fr_auto] gap-x-[0.8em] border-b pb-[0.5em] text-[0.78em] font-semibold uppercase tracking-wide', hair, muted)}>
          <span>Time</span>
          <span>Role</span>
          <span>Action</span>
          <span>Status</span>
        </div>
        {rows.map((r, i) => (
          <motion.div
            key={r[2]}
            className={cn('grid flex-1 grid-cols-[0.8fr_1fr_2fr_auto] items-center gap-x-[0.8em] border-b py-[0.7em]', hair)}
            initial={false}
            animate={seen ? { opacity: 1, x: 0 } : { opacity: 0, x: -12 }}
            transition={{ duration: 0.45, ease: OUT_EASE, delay: seen ? 0.25 + i * 0.12 : 0 }}
          >
            <span className={cn('font-[family-name:var(--font-landing-mono)] text-[0.88em] tabular-nums', muted)}>{r[0]}</span>
            <span className="font-semibold">{r[1]}</span>
            <span>{r[2]}</span>
            <span className="px-[0.5em] py-[0.1em] text-[0.75em] font-bold uppercase text-white" style={{ backgroundColor: BRAND.green }}>
              Logged
            </span>
          </motion.div>
        ))}
      </div>
    </Stage>
  )
}

/* -------------------------------- taskpane ------------------------------- */
function TaskpaneFragment({ title, active }: UiFragmentProps) {
  const seen = useSeen(active)
  const assets = /document creation|asset/i.test(title)
  const items = assets
    ? ['Logo, full colour', 'Product image', 'Brand colours', 'Brand fonts']
    : ['Proposal', 'Sales deck', 'Follow-up email', 'Pricing sheet']
  return (
    <Stage accent="pink">
      <WindowBar app="word" title="Word" meta="Templatean add-in" />
      <div className="grid flex-1 grid-cols-[1.25fr_1fr]">
        <div className="flex flex-col gap-[0.6em] bg-[var(--color-landing-gray-50)] p-[1em] dark:bg-[#15151d]">
          <span className={cn(line, 'w-2/3 bg-[var(--color-landing-gray-900)] dark:bg-white')} />
          {[100, 92, 96].map((w, i) => (
            <span key={i} className={line} style={{ width: `${w}%` }} />
          ))}
          <motion.span
            className="block h-[24%] w-full origin-top"
            style={{ backgroundColor: assets ? BRAND.blue : BRAND.purple, ...halftone(0.28, 5) }}
            initial={false}
            animate={{ scaleY: seen ? 1 : 0, opacity: seen ? 1 : 0 }}
            transition={{ duration: 0.55, ease: OUT_EASE, delay: seen ? 0.9 : 0 }}
          />
          {[88, 100, 64, 0, 96, 100, 80, 92, 58].map((w, i) => (
            <span key={i} className={line} style={{ width: `${w}%` }} />
          ))}
        </div>
        <div className={cn('flex flex-col gap-[0.7em] border-l p-[0.9em]', hair)}>
          <span className="font-semibold">{assets ? 'Brand assets' : 'Templates'}</span>
          <span className={cn('flex items-center gap-[0.4em] border px-[0.5em] py-[0.35em] text-[0.85em]', hair, muted)}>
            <Search className="h-[1em] w-[1em]" /> Search
          </span>
          {items.map((item, i) => (
            <motion.span
              key={item}
              className={cn('flex items-center gap-[0.6em] border p-[0.5em]', hair)}
              initial={false}
              animate={
                seen && i === 0
                  ? { borderColor: 'rgba(12,0,255,0.9)', backgroundColor: 'rgba(12,0,255,0.06)' }
                  : { borderColor: 'rgba(160,160,160,0.35)', backgroundColor: 'rgba(12,0,255,0)' }
              }
              transition={{ duration: 0.35, ease: OUT_EASE, delay: seen ? 0.45 : 0 }}
            >
              <span
                className="block h-[2em] w-[1.6em] shrink-0"
                style={{ backgroundColor: [BRAND.blue, BRAND.purple, BRAND.pink, BRAND.green][i], ...halftone(0.3, 4) }}
              />
              <span className="flex-1 text-[0.88em] font-medium leading-tight">{item}</span>
            </motion.span>
          ))}
          <span className="mt-auto bg-[var(--color-landing-primary)] py-[0.5em] text-center text-[0.82em] font-bold uppercase text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
            Insert
          </span>
        </div>
      </div>
    </Stage>
  )
}

/* --------------------------------- sheet --------------------------------- */
function SheetFragment({ title, active }: UiFragmentProps) {
  const seen = useSeen(active)
  const formula = '=SUMIFS(Sales[Amount], Sales[Region], A2, Sales[Quarter], "Q4")'
  const { shown } = useStream(formula, active)
  const cols = ['A', 'B', 'C', 'D', 'E']
  const heads = ['Region', 'Q1', 'Q2', 'Q3', 'Q4']
  return (
    <Stage accent="green">
      <WindowBar app="excel" title={title} meta="Excel" />
      <div className={cn('flex items-center gap-[0.6em] border-b px-[1em] py-[0.5em] font-[family-name:var(--font-landing-mono)] text-[0.85em]', hair)}>
        <span className="font-semibold italic text-[var(--color-landing-gray-500)]">fx</span>
        <span className="truncate">{shown}</span>
      </div>
      <div className="relative flex-1 p-[0.8em]">
        <div className="grid grid-cols-[1.6em_repeat(5,1fr)] text-[0.82em]">
          <span />
          {cols.map((c) => (
            <span key={c} className={cn('border-b border-r py-[0.2em] text-center font-semibold', hair, muted)}>{c}</span>
          ))}
          {Array.from({ length: 10 }).map((_, r) => (
            <div key={r} className="contents">
              <span className={cn('border-b border-r py-[0.45em] text-center', hair, muted)}>{r + 1}</span>
              {cols.map((c, ci) => (
                <span
                  key={c}
                  className={cn(
                    'flex items-center border-b border-r px-[0.4em] py-[0.45em]',
                    hair,
                    r === 0 && 'font-semibold',
                    r === 3 && ci === 4 && 'outline outline-2 -outline-offset-2 outline-[var(--color-landing-accent)]'
                  )}
                >
                  {r === 0 ? heads[ci] : <span className={cn(line, ci === 0 ? 'w-3/4' : 'w-1/2')} />}
                </span>
              ))}
            </div>
          ))}
        </div>
        <motion.div
          className="absolute bottom-[6%] right-[5%] w-[58%] bg-[var(--color-landing-primary)] p-[0.8em] text-[0.85em] text-white dark:bg-white dark:text-[var(--color-landing-primary)]"
          initial={false}
          animate={seen ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ duration: 0.5, ease: OUT_EASE, delay: seen ? 1.2 : 0 }}
        >
          <span className="flex items-center gap-[0.4em] font-semibold">
            <Sparkles className="h-[1.1em] w-[1.1em]" /> Explained
          </span>
          <span className="mt-[0.3em] block opacity-80">Adds every Q4 sale whose region matches A2.</span>
        </motion.div>
      </div>
    </Stage>
  )
}

/* ---------------------------------- mail --------------------------------- */
function MailFragment({ active }: UiFragmentProps) {
  const seen = useSeen(active)
  return (
    <Stage accent="blue">
      <WindowBar app="outlook" title="New message" meta="Outlook" />
      <div className="flex flex-1 flex-col gap-[0.8em] p-[1.2em]">
        {['To', 'Subject'].map((f, i) => (
          <span key={f} className={cn('flex items-center gap-[0.8em] border-b pb-[0.6em]', hair)}>
            <span className={cn('w-[4.5em] text-[0.85em] font-semibold', muted)}>{f}</span>
            <span className={cn(line, i ? 'w-1/2' : 'w-1/3')} />
          </span>
        ))}
        <span className="flex gap-[0.5em] text-[0.8em] font-semibold uppercase">
          <span className="flex items-center gap-[0.35em] bg-[var(--color-landing-accent)] px-[0.6em] py-[0.25em] text-white">
            <Sparkles className="h-[1em] w-[1em]" /> AI composer
          </span>
          <span className="bg-[var(--color-landing-gray-100)] px-[0.6em] py-[0.25em] dark:bg-[#15151d]">Templates</span>
        </span>
        <span className="flex flex-col gap-[0.7em] pt-[0.3em]">
          {[64, 100, 92, 97, 58, 88, 40, 0, 76, 100, 84, 52].map((w, i) => (
            <motion.span
              key={i}
              className={cn(line, 'origin-left')}
              style={{ width: `${w}%` }}
              initial={false}
              animate={{ scaleX: seen ? 1 : 0 }}
              transition={{ duration: 0.4, ease: OUT_EASE, delay: seen ? 0.3 + i * 0.14 : 0 }}
            />
          ))}
        </span>
        <span className="mt-auto self-start bg-[var(--color-landing-primary)] px-[1.2em] py-[0.5em] text-[0.85em] font-bold uppercase text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
          Send
        </span>
      </div>
    </Stage>
  )
}

/* --------------------------------- roles --------------------------------- */
function RolesFragment({ title, active }: UiFragmentProps) {
  const seen = useSeen(active)
  const partner = /partner/i.test(title)
  const roles: Array<[string, boolean[]]> = partner
    ? [
        ['Partner', [true, true, false, false]],
        ['Marketing', [true, true, true, false]],
        ['Brand lead', [true, true, true, true]],
      ]
    : [
        ['Viewer', [true, false, false, false]],
        ['Editor', [true, true, true, false]],
        ['Approver', [true, true, true, true]],
        ['Admin', [true, true, true, true]],
      ]
  const actions = ['View', 'Use', 'Edit', 'Approve']
  return (
    <Stage accent="pink">
      <WindowBar icon={<Lock className="h-[1.3em] w-[1.3em] text-[var(--color-landing-accent)]" />} title="Access" meta={title} />
      <div className="flex flex-1 flex-col p-[1.1em]">
        <div className={cn('grid grid-cols-[1.6fr_repeat(4,1fr)] border-b pb-[0.5em] text-[0.78em] font-semibold uppercase tracking-wide', hair, muted)}>
          <span>Role</span>
          {actions.map((a) => (
            <span key={a} className="text-center">{a}</span>
          ))}
        </div>
        {roles.map(([role, allowed], r) => (
          <div key={role} className={cn('grid flex-1 grid-cols-[1.6fr_repeat(4,1fr)] items-center border-b py-[0.9em]', hair)}>
            <span className="font-semibold">{role}</span>
            {allowed.map((ok, c) => (
              <span key={c} className="flex justify-center">
                {ok ? (
                  <Tick show={seen} delay={0.25 + r * 0.14 + c * 0.05} />
                ) : (
                  <Minus className={cn('h-[1.1em] w-[1.1em]', muted)} />
                )}
              </span>
            ))}
          </div>
        ))}
      </div>
    </Stage>
  )
}

const FRAGMENTS: Record<UiFragmentKind, (p: UiFragmentProps) => ReactNode> = {
  prompt: PromptFragment,
  form: FormFragment,
  chart: ChartFragment,
  locked: LockedFragment,
  flow: FlowFragment,
  assets: AssetsFragment,
  audit: AuditFragment,
  taskpane: TaskpaneFragment,
  sheet: SheetFragment,
  mail: MailFragment,
  roles: RolesFragment,
}

/** Renders the product UI fragment for a use case. */
export function UiFragment(props: UiFragmentProps) {
  const Component = FRAGMENTS[props.kind]
  return <Component {...props} />
}

export default UiFragment
